import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import MetaballCanvas from '../components/MetaballCanvas';
import LightModeCanvas from '../components/LightModeCanvas';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import GlowCard from '../components/GlowCard';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useTheme } from '../context/ThemeContext';

const ScrollRevealSection = ({ children, className = '', delay = 0 }) => {
  const ref = useScrollReveal({ threshold: 0.1, triggerOnce: true });
  
  return (
    <div 
      ref={ref} 
      className={`scroll-reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const services = [
    'Custom Websites',
    'Mobile Applications', 
    'Full-Stack Development',
    'Business Solutions'
  ];

  return (
    <div className={`relative min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-white text-gray-900'} overflow-hidden`}>
      {isDarkMode ? <MetaballCanvas /> : <LightModeCanvas />}
      <Navigation />
      
      <main className="relative z-10">
        <section className="min-h-screen flex flex-col">
          <div 
            className="flex-1 flex items-center justify-center px-8"
            style={{
              transform: `translateY(${scrollY * 0.5}px)`,
              opacity: 1 - scrollY / 800,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div className="max-w-5xl mx-auto text-center">
              <h1 
                className={`hero-title text-6xl md:text-8xl lg:text-9xl font-light leading-[0.9] mb-8 tracking-tight cursor-default ${
                  isDarkMode ? 'text-white' : 'text-gray-900 hero-title-light'
                }`}
                style={{
                  transform: `translateY(${scrollY * 0.3}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                Your idea.<br />Our mission.
              </h1>
              <p 
                className={`text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
                style={{
                  transform: `translateY(${scrollY * 0.4}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                We turn vision into beautifully crafted digital experiences.
              </p>
            </div>
          </div>

          <div 
            className="px-8 pb-8"
            style={{
              transform: `translateY(${scrollY * 0.2}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end space-y-8 md:space-y-0">
                <Link to="/contact" className="block group">
                  <p className={`text-xs uppercase tracking-wider mb-2 transition-colors duration-300 ${
                    isDarkMode 
                      ? 'text-gray-400 group-hover:text-white' 
                      : 'text-gray-500 group-hover:text-gray-900'
                  }`}>+Get in touch</p>
                  <div className={`text-sm transition-colors duration-300 inline-flex items-center ${
                    isDarkMode 
                      ? 'text-white hover:text-gray-300' 
                      : 'text-gray-900 hover:text-gray-600'
                  }`}>
                    onlzCode
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>

                <div className="flex flex-wrap gap-4">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service === 'Contact' ? '/contact' : '/portfolio'}
                      className={`text-sm transition-all duration-300 relative group ${
                        isDarkMode 
                          ? 'text-gray-400 hover:text-white' 
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <span className="relative z-10">{service}</span>
                      <span className={`absolute bottom-0 left-0 w-0 h-px transition-all duration-300 group-hover:w-full ${
                        isDarkMode ? 'bg-white' : 'bg-gray-900'
                      }`} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={`relative z-20 min-h-screen py-20 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto px-8">
            <ScrollRevealSection className="text-center mb-16">
              <div className={`text-xs uppercase tracking-[0.3em] mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>What We Do</div>
              <h2 className={`text-4xl md:text-6xl font-light mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Crafting Digital<br />Excellence
              </h2>
              <p className={`text-base max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                From concept to deployment, we build solutions that matter
              </p>
            </ScrollRevealSection>

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {[
                { icon: '01', title: 'Web Development', desc: 'Custom websites & web applications' },
                { icon: '02', title: 'Mobile Apps', desc: 'iOS & Android solutions' },
                { icon: '03', title: 'UI/UX Design', desc: 'User-centered design systems' },
                { icon: '04', title: 'Cloud Solutions', desc: 'Scalable infrastructure' }
              ].map((service, index) => (
                <ScrollRevealSection key={index} delay={index * 100}>
                  <div className={`group p-6 border transition-all duration-500 hover:-translate-y-2 ${
                    isDarkMode 
                      ? 'border-white/10 hover:border-white/30 bg-black/30' 
                      : 'border-gray-200 hover:border-gray-400 bg-white/50 shadow-sm hover:shadow-lg'
                  }`}>
                    <div className={`text-3xl font-light mb-4 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-600 group-hover:text-white' : 'text-gray-300 group-hover:text-gray-900'
                    }`}>{service.icon}</div>
                    <h3 className={`text-lg font-light mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{service.title}</h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{service.desc}</p>
                  </div>
                </ScrollRevealSection>
              ))}
            </div>

            {/* Stats Section */}
            <div className={`border-t border-b py-12 mb-12 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ScrollRevealSection delay={0}>
                <GlowCard 
                  className={`border backdrop-blur-sm transition-all duration-500 group ${
                    isDarkMode 
                      ? 'border-white/10 bg-black/50' 
                      : 'border-gray-200 bg-white/80 shadow-lg'
                  }`}
                  glowColor={isDarkMode ? "rgba(138, 100, 255, 0.6)" : "rgba(99, 102, 241, 0.3)"}
                  glowSize={250}
                >
                  <div className="p-8 text-center">
                    <div className={`text-5xl font-light mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>150+</div>
                    <h3 className={`text-sm uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Projects Delivered</h3>
                  </div>
                </GlowCard>
              </ScrollRevealSection>

              <ScrollRevealSection delay={100}>
                <GlowCard 
                  className={`border backdrop-blur-sm transition-all duration-500 group ${
                    isDarkMode 
                      ? 'border-white/10 bg-black/50' 
                      : 'border-gray-200 bg-white/80 shadow-lg'
                  }`}
                  glowColor={isDarkMode ? "rgba(100, 200, 255, 0.6)" : "rgba(56, 189, 248, 0.3)"}
                  glowSize={250}
                >
                  <div className="p-8 text-center">
                    <div className={`text-5xl font-light mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>8+</div>
                    <h3 className={`text-sm uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Years Experience</h3>
                  </div>
                </GlowCard>
              </ScrollRevealSection>

              <ScrollRevealSection delay={200}>
                <GlowCard 
                  className={`border backdrop-blur-sm transition-all duration-500 group ${
                    isDarkMode 
                      ? 'border-white/10 bg-black/50' 
                      : 'border-gray-200 bg-white/80 shadow-lg'
                  }`}
                  glowColor={isDarkMode ? "rgba(255, 150, 100, 0.6)" : "rgba(251, 146, 60, 0.3)"}
                  glowSize={250}
                >
                  <div className="p-8 text-center">
                    <div className={`text-5xl font-light mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>80+</div>
                    <h3 className={`text-sm uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Happy Clients</h3>
                  </div>
                </GlowCard>
              </ScrollRevealSection>
              </div>
            </div>

            <ScrollRevealSection delay={300} className="text-center">
              <Link
                to="/portfolio"
                className={`inline-block px-8 py-4 border transition-all duration-300 ${
                  isDarkMode 
                    ? 'border-white/20 text-white hover:bg-white hover:text-black' 
                    : 'border-gray-300 text-gray-900 hover:bg-gray-900 hover:text-white'
                }`}
              >
                View All Projects
              </Link>
            </ScrollRevealSection>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
